import React, { useEffect, useMemo, useRef } from 'react';
import '../phases/EmailTask.css';
import './OutlookShell.css';

/**
 * One Outlook shell for the calibration and main arms (advisor §3.1). The markup and
 * class names reproduce the PI's original EmailTask.tsx (header, toolbar, folder pane,
 * message list, reading pane, decision-bar slot) and are styled by EmailTask.css. The
 * only additions are the toolbar card button, the scenario-clock caption, the
 * right-docked card panel, the in-client status bar and the toast (OutlookShell.css).
 * The legacy EmailTask keeps its own copy of the markup and is not touched.
 */

export interface ShellItem {
  id: string;
  sender: string;
  subject: string;
  preview: string;
  time: string;
  unread: boolean;
  decided: boolean;
}

export interface OutlookShellProps {
  decided: number;
  total: number;
  unreadCount: number;
  items: ShellItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  cardOpen: boolean;
  onToggleCard: () => void;
  /** Caption in the reading-pane toolbar row (advisor §3.4). */
  scenarioClock?: string;
  /** The selected message is already handled: grey "Handled" caption, no decision bar. */
  handled?: boolean;
  /** Reading-pane content (the message, or the browser-window layer). */
  children: React.ReactNode;
  /** Set when the reading pane should host a full-height layer instead of the scrolling content wrapper. */
  layer?: boolean;
  onPaneScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  decisionBar?: React.ReactNode;
  statusText?: string | null;
  toast?: string | null;
  cardPanel?: React.ReactNode;
  /** Called when a decorative toolbar button is pressed (the shell shows a toast). */
  onDisabledAction?: () => void;
}

const Folder = ({ d }: { d: string }) => (
  <svg className="folder-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d={d} /></svg>
);
const ICON = {
  inbox: 'M14 5.5V13.5C14 14.33 13.33 15 12.5 15H3.5C2.67 15 2 14.33 2 13.5V5.5L8 10L14 5.5ZM12.5 4C13.33 4 14 4.67 14 5.5L8 10L2 5.5C2 4.67 2.67 4 3.5 4H12.5Z',
  folder: 'M13 4H10L9 3H3C2.45 3 2 3.45 2 4V12C2 12.55 2.45 13 3 13H13C13.55 13 14 12.55 14 12V5C14 4.45 13.55 4 13 4Z',
  sent: 'M2 4L8 9L14 4V5.5L8 10.5L2 5.5V4M2 4C2 3.45 2.45 3 3 3H13C13.55 3 14 3.45 14 4L8 9L2 4Z',
  deleted: 'M12 3.5H10.5V3C10.5 2.45 10.05 2 9.5 2H6.5C5.95 2 5.5 2.45 5.5 3V3.5H4C3.45 3.5 3 3.95 3 4.5V5H13V4.5C13 3.95 12.55 3.5 12 3.5ZM12 6H4L4.5 13.5C4.5 14.05 4.95 14.5 5.5 14.5H10.5C11.05 14.5 11.5 14.05 11.5 13.5L12 6Z',
  more: 'M8 2C8.55 2 9 2.45 9 3C9 3.55 8.55 4 8 4C7.45 4 7 3.55 7 3C7 2.45 7.45 2 8 2ZM8 6C8.55 6 9 6.45 9 7C9 7.55 8.55 8 8 8C7.45 8 7 7.55 7 7C7 6.45 7.45 6 8 6ZM8 10C8.55 10 9 10.45 9 11C9 11.55 8.55 12 8 12C7.45 12 7 11.55 7 11C7 10.45 7.45 10 8 10Z',
};

const OutlookShell: React.FC<OutlookShellProps> = ({
  decided, total, unreadCount, items, selectedId, onSelect, cardOpen, onToggleCard, scenarioClock, handled,
  children, layer, onPaneScroll, decisionBar, statusText, toast, cardPanel, onDisabledAction,
}) => {
  const listExpanded = false; // list is never collapsed in the study (toggle omitted)
  const clientRef = useRef<HTMLDivElement>(null);
  const noop = (e: React.MouseEvent) => { e.preventDefault(); onDisabledAction?.(); };
  const pct = total ? Math.round((decided / total) * 100) : 0;

  // Focus returns to the inbox when a modal closes (advisor §8 keyboard).
  useEffect(() => {
    const el = clientRef.current;
    if (!el) return;
    const onFocusReturn = () => { if (document.activeElement === document.body) el.focus({ preventScroll: true }); };
    document.addEventListener('study-modal-closed', onFocusReturn);
    return () => document.removeEventListener('study-modal-closed', onFocusReturn);
  }, []);

  return (
    <div className="email-task outlook-shell">
      <div className={`email-client${cardOpen ? ' card-open' : ''}`} ref={clientRef} tabIndex={-1}>
        <div className="email-header">
          <h2>Outlook Mail</h2>
          <div className="email-progress">
            <span className="progress-text">Decisions made: {decided}/{total}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${pct}%` }}></div>
            </div>
          </div>
        </div>

        {/* Outlook-style toolbar — decorative except the card button. */}
        <div className="email-toolbar">
          <button className="toolbar-button primary" onClick={noop}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 2C8.27614 2 8.5 2.22386 8.5 2.5V7.5H13.5C13.7761 7.5 14 7.72386 14 8C14 8.27614 13.7761 8.5 13.5 8.5H8.5V13.5C8.5 13.7761 8.27614 14 8 14C7.72386 14 7.5 13.7761 7.5 13.5V8.5H2.5C2.22386 8.5 2 8.27614 2 8C2 7.72386 2.22386 7.5 2.5 7.5H7.5V2.5C7.5 2.22386 7.72386 2 8 2Z" />
            </svg>
            New mail
          </button>
          <button className="toolbar-button" onClick={noop}>Reply</button>
          <button className="toolbar-button" onClick={noop}>Reply all</button>
          <button className="toolbar-button" onClick={noop}>Forward</button>
          <div className="toolbar-separator"></div>
          <button className="toolbar-button" onClick={noop}>Delete</button>
          <button className="toolbar-button" onClick={noop}>Report spam</button>
          <div className="toolbar-right">
            <button type="button" className={`toolbar-button card-button${cardOpen ? ' active' : ''}`} data-testid="card-toggle" aria-pressed={cardOpen} onClick={onToggleCard}>
              My job and systems
            </button>
          </div>
        </div>

        <div className="email-container">
          <div className="folder-pane">
            <div className="folder-header">Folders</div>
            <div className="folder-item active">
              <Folder d={ICON.inbox} /><span>Inbox</span><span className="folder-count">{unreadCount}</span>
            </div>
            <div className="folder-item"><Folder d={ICON.folder} /><span>Drafts</span></div>
            <div className="folder-item"><Folder d={ICON.sent} /><span>Sent Items</span></div>
            <div className="folder-item"><Folder d={ICON.deleted} /><span>Deleted Items</span></div>
            <div className="folder-item"><Folder d={ICON.folder} /><span>Archive</span></div>
            <div className="folder-item"><Folder d={ICON.more} /><span>More</span></div>
          </div>

          <div className={`email-list ${listExpanded ? 'expanded' : 'normal'}`}>
            <div className="email-list-header">
              <div className="list-controls">
                <div className="filter-dropdown">All</div>
                <div className="list-actions">
                  <button className="icon-button" title="Filter" onClick={noop}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M14 3H2L7 9V13L9 14V9L14 3Z" /></svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="email-items-container">
              {items.map(it => (
                <div
                  key={it.id}
                  className={`email-item ${selectedId === it.id ? 'selected' : ''} ${it.unread ? 'unread' : ''} ${it.decided ? 'decided' : ''}`}
                  data-email-id={it.id}
                  onClick={() => onSelect(it.id)}
                >
                  <div className="email-item-content">
                    <div className="email-avatar">{it.sender.charAt(0).toUpperCase()}</div>
                    <div className="email-info">
                      <div className="email-sender">{it.sender}</div>
                      <div className="email-subject">{it.subject}</div>
                      <div className="email-preview">{it.preview}</div>
                    </div>
                    <div className="email-time">{it.time}</div>
                  </div>
                </div>
              ))}
            </div>
            {/* The original's list-collapse toggle is omitted: a global button style renders it as an
                empty 44 px grey square over the list, and one-at-a-time arrival never needs it. */}
          </div>

          <div className="email-detail">
            <div className="email-detail-header">
              <div className="detail-toolbar">
                <button className="toolbar-button" onClick={noop}>Reply</button>
                <button className="toolbar-button" onClick={noop}>Reply all</button>
                <button className="toolbar-button" onClick={noop}>Forward</button>
                <div className="toolbar-separator"></div>
                <button className="toolbar-button" onClick={noop}>Delete</button>
                <button className="toolbar-button" onClick={noop}>Report spam</button>
                {handled && <span className="handled-caption" data-testid="handled-caption">Handled</span>}
                {scenarioClock && <span className="scenario-clock" data-testid="scenario-clock">{scenarioClock}</span>}
              </div>
            </div>
            {layer ? children : (
              <div className="email-detail-content-wrapper" onScroll={onPaneScroll}>{children}</div>
            )}
            {decisionBar}
          </div>

          {cardOpen && cardPanel}
        </div>

        {statusText && <div className="email-statusbar" data-testid="status-bar">{statusText}</div>}
        {toast && <div className="outlook-toast" role="status">{toast}</div>}
      </div>
    </div>
  );
};

export default OutlookShell;

/* ---------- The message itself, rendered exactly as the reading pane does ---------- */

export interface EmailMessageProps {
  subject: string;
  senderName: string;
  senderAddress: string;
  /** Scenario time line under the sender (never today's date). */
  timeLine: string;
  /** Full HTML body including the <div class="email-body"> wrapper (calibrationBank.toFullContent). */
  html: string;
  onLinkEnter?: (url: string) => void;
  onLinkLeave?: (url: string) => void;
  onLinkClick?: (url: string) => void;
}

export const EmailMessage: React.FC<EmailMessageProps> = ({ subject, senderName, senderAddress, timeLine, html, onLinkEnter, onLinkLeave, onLinkClick }) => {
  const avatar = senderName.charAt(0).toUpperCase();
  // React 19 re-assigns innerHTML whenever the {__html} object identity changes (it no longer
  // compares the string), which would replace the link element on every re-render and make the
  // browser fire a fresh mouseover — double-counting hovers. Keep the object stable per html.
  const htmlObj = useMemo(() => ({ __html: html }), [html]);
  const over = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    if (!el.classList.contains('email-link') || el.getAttribute('data-hovering')) return;
    el.setAttribute('data-hovering', '1');
    onLinkEnter?.(el.getAttribute('data-url') || '');
  };
  const out = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    if (!el.classList.contains('email-link') || !el.getAttribute('data-hovering')) return;
    el.removeAttribute('data-hovering');
    onLinkLeave?.(el.getAttribute('data-url') || '');
  };
  const click = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    if (!el.classList.contains('email-link')) return;
    e.preventDefault();
    onLinkClick?.(el.getAttribute('data-url') || '');
  };
  return (
    <>
      <div className="email-detail-content">
        <h3 className="email-detail-subject">{subject}</h3>
        <div className="email-detail-metadata">
          <div className="sender-avatar">{avatar}</div>
          <div className="sender-info">
            <div className="sender-line">
              <span className="email-detail-sender">{senderName}</span>
              <span className="email-detail-address">&lt;{senderAddress}&gt;</span>
            </div>
            <div className="recipient-line">To: You</div>
            <div className="email-detail-time">{timeLine}</div>
          </div>
        </div>
      </div>
      <div className="email-detail-body">
        <div className="email-body-host" dangerouslySetInnerHTML={htmlObj} onMouseOver={over} onMouseOut={out} onClick={click} />
      </div>
    </>
  );
};
